import { useEffect, useRef, useState } from "react";
import { IConversation } from "./modal";
import { useNotification } from "../../shared/NotificatonContext";
import { Button } from "primereact/button";
import { formatChatSepratorDate, formatMessageTime } from "./JVConversations";
import "./jvchat.css";
import { baseUrl, hrLenseBaseUrl } from "../../shared/Fetch";
import profilePic from "../../assets/Images/profile_pic_placeholder.jpg";
import { Dialog } from "primereact/dialog";
import tekLenseLogo from "../../assets/Images/tekLenseLogo.png";
import { format } from "date-fns";
import JVMessage, { TagContent, parseTextBlocks } from "./JVMessage";
import { UserInfo } from "./UserInfo";

export default function JVChat({
    conversation,
    myId,
    startConversation,
}: {
    conversation: IConversation;
    myId: number;
    startConversation: (userId: number) => Promise<void>
}) {

    const { connection, isOnline, req, setReq } = useNotification();
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [input, setInput] = useState("");
    const [selectedFile, setSelectedFile] = useState<any>(null);

    const [loading, setLoading] = useState(false);
    const [showUser, setShowUser] = useState(false);

    const [view, setView] = useState<{ name?: string, url?: string } | null>(null);

    function getDateString(date?: string) {
        if (date) {
            try {
                return format(new Date(date), "MM-dd-yyyy");
            } catch {
                return '-';
            }
        } else {
            return '-';
        }
    }

    const reqText = (req && req.userId === conversation.userId)
        ? req.eciId ? `<eci>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</eci>`
            : req.subId ? `<pip>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</pip>`
                : `<req>${req.reqId} | ${req.jobTitle} | ${getDateString(req.postedDate)}</req>`
        : "";

    const sendMessage = async () => {
        if (!(input.trim() || selectedFile) || !connection || loading) return;

        setLoading(true);

        try {
            let fileData: any = null;

            if (selectedFile) {
                const formData = new FormData();
                formData.append("file", selectedFile);

                const res = await fetch(`${baseUrl}Experiment/upload`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${JSON.parse(sessionStorage.getItem('auth') || "")?.token}`
                    },
                    body: formData
                });

                if (!res.ok) {
                    throw new Error("File upload failed");
                }

                fileData = await res.json();
            }

            if (conversation.isGroup) {
                await connection.invoke(
                    "SendGroupMessage",
                    conversation.groupId,
                    reqText + input.trim(),
                    fileData?.fileUrl,
                    fileData?.fileName,
                    fileData?.contentType
                );
            } else {
                await connection.invoke(
                    "SendMessage",
                    conversation.userId,
                    reqText + input.trim(),
                    fileData?.fileUrl,
                    fileData?.fileName,
                    fileData?.contentType
                );
            }

            setInput("");
            setReq(undefined);
            setSelectedFile(null);
        } catch (err) {
            console.error(err);
            alert("Failed to send message");
        } finally {
            setLoading(false);
        }
    };

    async function startNewConversation(userId: number) {
        if (loading) return;
        setLoading(true);
        await startConversation(userId);
        setLoading(false);
    }

    useEffect(() => {
        bottomRef.current?.scrollIntoView({
            behavior: "auto"
        });
    }, [conversation.id]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({
            behavior: "smooth"
        });
    }, [conversation.messages?.length]);

    const selectFile = async (e: any) => {
        const file = e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
    };

    function getFileLogo(fileType?: string, isMine?: boolean) {
        if (!fileType) return <i className="pi pi-file" style={{ fontSize: '1.2rem' }} />;

        const type = fileType.toLowerCase();
        let icon = "file";

        if (type.includes("pdf")) icon = "file-pdf";
        else if (type.includes("word") || type.includes("doc")) icon = "file-word";
        else if (type.includes("excel") || type.includes("sheet") || type.includes("xls")) icon = "file-excel";
        else if (type.includes("text")) icon = "file-edit";

        let color = "#6b7280";

        if (isMine) {
            color = "rgba(255,255,255,0.95)";
        } else {
            color =
                icon === "file-pdf" ? "#e53935" :
                    icon === "file-excel" ? "#2e7d32" :
                        icon === "file-word" ? "#1976d2" :
                            "#6b7280";
        }

        return (
            <i className={`pi pi-${icon}`} style={{ fontSize: "1.2rem", color }} />
        );
    }

    return (
        <>
            <div style={styles.chatContainer}>
                {conversation.isGroup
                    ?
                    <div style={styles.chatHeader}>
                        <div style={styles.groupAvatar}>
                            {conversation.groupName === 'Global Chat'
                                ? <img src={tekLenseLogo}
                                    style={styles.avatarImg} />
                                : conversation.groupName?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div style={styles.name}>{conversation.groupName}</div>
                        </div>
                    </div>
                    :
                    <div style={styles.chatHeader}>
                        <div style={styles.avatar}><img onClick={() => setShowUser(true)}
                            src={conversation.user?.employeeId ? `${hrLenseBaseUrl}/Hrlense_Employee/getEmployeeImages?EmployeeCode=${conversation.user.employeeId}`
                                : profilePic}
                            onError={e => e.currentTarget.src = profilePic}
                            style={styles.avatarImg} />
                            {isOnline(conversation.userId!) && (<span style={styles.onlineDot}></span>)}
                        </div>
                        <div>
                            <div style={styles.name}>{conversation.user!.name}</div>
                            <div style={styles.email}>{conversation.user!.email} | {conversation.user!.companyName} ({conversation.user!.branchName})</div>
                        </div>
                    </div>}

                <div style={styles.messages}>
                    {!conversation.messages && <div className="text-center">Loading...</div>}
                    {conversation.messages?.map((message, index) => {
                        const prevMessage = conversation.messages?.[index - 1];
                        const showDaySeparator = !prevMessage
                            || new Date(prevMessage.createdAt).toDateString()
                            !== new Date(message.createdAt).toDateString();

                        const isMine = message.senderId === myId;
                        const isImage = message.fileUrl && message.fileType?.startsWith("image");

                        return (
                            <div key={message.id}>
                                {showDaySeparator && (
                                    <div style={styles.daySeparator}>
                                        {formatChatSepratorDate(message.createdAt)}
                                    </div>
                                )}
                                <div className="jv-message"
                                    style={{
                                        ...styles.messageRow,
                                        justifyContent: isMine ? "flex-end" : "flex-start"
                                    }}
                                >
                                    {(!isMine && conversation.isGroup) &&
                                        <div style={styles.senderAvatar}>
                                            {message.sender!.employeeId
                                                ? <img
                                                    src={`${hrLenseBaseUrl}/Hrlense_Employee/getEmployeeImages?EmployeeCode=${message.sender!.employeeId}`}
                                                    onError={e => e.currentTarget.src = profilePic}
                                                    style={styles.avatarImg} />
                                                : message.sender!.name?.charAt(0).toUpperCase()}
                                        </div>
                                    }
                                    {isMine && <div className="jv-message-time" style={{ marginRight: 6 }}>{formatMessageTime(message.createdAt)}</div>}
                                    <div style={{
                                        ...styles.messageBubble,
                                        backgroundColor: isMine ? "#4e73df" : "#e4e6eb",
                                        padding: isImage ? '2px 3px' : "10px 15px",
                                        borderRadius: isImage ? 10 : 20,
                                        color: isMine ? "white" : "black",
                                        overflow: "hidden",
                                    }}>
                                        {(!isMine && conversation.isGroup) && <div style={styles.senderName}>{message.sender.name}</div>}
                                        {message.fileUrl && (
                                            <div>
                                                {isImage && (
                                                    <img onClick={() => setView({ name: message.fileName, url: `${baseUrl}${message.fileUrl}` })}
                                                        src={`${baseUrl}${message.fileUrl}`}
                                                        style={{
                                                            borderRadius: 10,
                                                            maxHeight: '250px',
                                                            width: '100%',
                                                            cursor: "pointer",
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}

                                                {!isImage && message.fileUrl && (
                                                    <a
                                                        href={`${baseUrl}${message.fileUrl}`}
                                                        target="_blank"
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                            color: isMine ? "white" : "#1d9bf0",
                                                            textDecoration: "none",
                                                            fontWeight: 500,
                                                            marginBottom: message.message ? 6 : 0,
                                                        }}
                                                    >
                                                        {getFileLogo(message.fileType, isMine)} {message.fileName}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {message.message && <JVMessage isImage={isImage ? true : false} isMine={isMine} text={message.message} />}
                                    </div>
                                    {!isMine && <div className="jv-message-time" style={{ marginLeft: 6 }}>{formatMessageTime(message.createdAt)}</div>}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={bottomRef} />
                </div>


                <div style={styles.inputContainer}>
                    {conversation.id === 0 ?
                        <Button className="p-button-help p-button-rounded" loading={loading} onClick={() => startNewConversation(conversation.userId!)} style={styles.sendBtn} aria-label="Start Conversation" label="Start Conversation" />
                        : <>
                            <input
                                type="file"
                                id="fileInput"
                                style={{ display: "none" }}
                                onChange={selectFile}
                            />

                            <Button
                                icon="pi pi-paperclip"
                                className="p-button-text p-button-rounded mt-auto"
                                onClick={() => document.getElementById("fileInput")?.click()}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                {selectedFile && (
                                    <div style={styles.filePreview}>
                                        <div style={styles.fileRemove} onClick={() => setSelectedFile(null)}>✖</div>
                                        {selectedFile.type.startsWith("image") ?
                                            <img src={URL.createObjectURL(selectedFile)} style={styles.fileImage} />
                                            :
                                            <div style={styles.fileInfo}>{getFileLogo(selectedFile?.type)} {selectedFile.name}</div>
                                        }
                                    </div>
                                )}
                                {reqText && (
                                    <div style={styles.filePreview}>
                                        <div style={styles.fileRemove} onClick={() => setReq(undefined)}>✖</div>
                                        <TagContent {...parseTextBlocks(reqText)} />
                                    </div>
                                )}
                                <textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Type a message..."
                                    style={styles.input}
                                    rows={1}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            sendMessage();
                                        }
                                    }}
                                />
                            </div>

                            <Button className="p-button-help p-button-rounded mt-auto" icon="pi pi-send" loading={loading} onClick={sendMessage} style={styles.sendBtn} aria-label="Send" />
                        </>
                    }</div>
            </div>

            <Dialog visible={!!view} header={view?.name} onHide={() => setView(null)}>
                <div style={{
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                }}>
                    {view?.url
                        ? <img src={view?.url}
                            onError={e => e.currentTarget.src = profilePic}
                            style={{ maxHeight: '60vh', maxWidth: '95vw', objectFit: 'contain' }} />
                        : conversation.user?.name?.charAt(0).toUpperCase()}
                </div>
            </Dialog>

            {conversation.user && <UserInfo open={showUser} hideDialog={() => setShowUser(false)} user={conversation.user} />}
        </>
    );
}

const styles: Record<string, React.CSSProperties> = {
    chatContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column"
    },
    chatHeader: {
        padding: "15px 20px",
        borderBottom: "1px solid #ddd",
        display: "flex",
        alignItems: "center",
        gap: 10,
        backgroundColor: "white"
    },
    avatar: {
        width: 45,
        height: 45,
        borderRadius: "50%",
        backgroundColor: "#4e73df",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        position: 'relative',
    },
    senderAvatar: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "#4e73df",
        marginRight: 6,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        position: 'relative',
    },
    senderName: {
        color: '#4e73df',
        fontSize: 12,
        fontWeight: 'bold',
    },
    groupAvatar: {
        width: 45,
        height: 45,
        borderRadius: "50%",
        backgroundColor: "#28c76f",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        position: 'relative',
    },
    avatarImg: {
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        objectFit: "cover",
        cursor: 'pointer',
    },
    name: {
        fontWeight: 600
    },
    email: {
        fontWeight: 600,
        fontSize: 12
    },
    onlineDot: {
        position: "absolute",
        bottom: 2,
        right: 2,
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: "#22c55e",
        border: "2px solid white"
    },
    messages: {
        flex: 1,
        overflowY: "auto",
        padding: 20,
        backgroundColor: "#f5f7fb"
    },
    messageRow: {
        display: "flex",
        marginBottom: 10,
    },
    messageBubble: {
        maxWidth: "60%",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "pre-wrap",
    },
    daySeparator: {
        textAlign: "center",
        margin: "12px 0",
        fontSize: 12,
        color: "#666",
        background: "#e5e7eb",
        padding: "4px 10px",
        borderRadius: 12,
        width: "fit-content",
        marginLeft: "auto",
        marginRight: "auto"
    },
    inputContainer: {
        display: "flex",
        padding: 15,
        borderTop: "1px solid #ddd",
        backgroundColor: "white"
    },
    input: {
        flex: 1,
        padding: 10,
        borderRadius: 20,
        border: "1px solid #ccc",
        outline: "none",
        width: '100%',
        resize: "none",
        maxHeight: 120,
        overflowY: "auto",
    },
    sendBtn: {
        marginLeft: 10,
        backgroundColor: "#4e73df",
        border: 'none'
    },
    filePreview: {
        position: "relative",
        backgroundColor: "#f1f5f9",
        borderRadius: 12,
        padding: "8px 12px",
        marginBottom: 6,
        display: "inline-flex",
        alignItems: "center",
    },

    fileRemove: {
        position: "absolute",
        top: -6,
        right: -6,
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#ef4444",
        color: "white",
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer"
    },

    fileImage: {
        maxWidth: 150,
        maxHeight: 120,
        borderRadius: 10,
        objectFit: "cover"
    },

    fileInfo: {
        fontSize: 13,
        fontWeight: 500,
        color: "#333",
        wordBreak: "break-word"
    }
};